// pages/Register.js
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FaCut,
  FaUser,
  FaEnvelope,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaArrowRight,
  FaGoogle,
  FaFacebookF,
  FaCalendarCheck,
} from 'react-icons/fa';
import './Auth.css';
import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';


const getPasswordStrength = (password) => {
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };

  const passedCount = Object.values(checks).filter(Boolean).length;

  let label = 'Weak';
  let color = '#dc2626';

  if (passedCount === 4) {
    label = 'Strong';
    color = '#16a34a';
  } else if (passedCount === 3) {
    label = 'Medium';
    color = '#f59e0b';
  } else if (passedCount >= 1) {
    label = 'Weak';
    color = '#dc2626';
  }

  return { checks, passedCount, label, color };
};



function Register() {
  const navigate = useNavigate();
  const toast = useToast();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);


  const passwordStrength = getPasswordStrength(password);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!passwordStrength.checks.length) {
      toast('Password must be at least 8 characters', 'error');
      return;
    }
    if (!passwordStrength.checks.uppercase) {
      toast('Password must contain an uppercase letter', 'error');
      return;
    }
    if (!passwordStrength.checks.lowercase) {
      toast('Password must contain a lowercase letter', 'error');
      return;
    }
    if (!passwordStrength.checks.number) {
      toast('Password must contain a number', 'error');
      return;
    }

    setLoading(true);

    const payload = { name, email, password };
    console.log('Sending register request:', payload);

    try {
      const res = await axios.post(`${baseurl}/auth/register`, payload);

      console.log('Register response:', res.data);

      toast(res.data.message || 'Registration successful. Please verify your email.', 'success');
      setName('');
      setEmail('');
      setPassword('');

      setTimeout(() => {
        navigate('/');
      }, 2500);
    } catch (err) {
      console.log('Register error response:', err.response?.data || err.message);
      toast(err.response?.data?.message || 'Something went wrong. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="auth-page">
      <div className="auth-wrapper">
        {/* LEFT PANEL */}
        <div className="auth-visual">
          <div className="visual-top">
            <div className="brand-row">
              <span className="brand-icon-box">
                <FaCut />
              </span>
              <span className="brand-name">Glow & Style</span>
            </div>
            <div className="badge-pill">
              <FaCalendarCheck className="badge-icon" />
              SALON BILLING PLATFORM
            </div>

            <h1 className="visual-heading">
              Start running <br />
              your salon <br />
              <span>the smart way</span>
            </h1>

            <p className="visual-text">
              Create your account and set up billing, staff, and services
              in minutes — no paperwork, no hassle.
            </p>

            <div className="stats-row">
              <div className="stat-item">
                <h3>8K+</h3>
                <span>Salons Onboard</span>
              </div>
              <div className="stat-item">
                <h3>1.2M+</h3>
                <span>Bills Generated</span>
              </div>
              <div className="stat-item">
                <h3>50+</h3>
                <span>Cities</span>
              </div>
            </div>
          </div>

          <div className="testimonial-box">
            <p>
              "Setting up our salon on Glow & Style took less than ten
              minutes. Billing has never been this simple."
            </p>
            <div className="testimonial-user">
              <div className="avatar">AK</div>
              <div>
                <strong>Ankit Kapoor</strong>
                <span>Manager, Urban Cuts</span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="auth-form-side">
          <div className="auth-form-box">
            <div className="form-head">
              <h2>Create your account</h2>
              <p>
                Already have an account? <Link to="/">Sign in →</Link>
              </p>
            </div>

          
            <form onSubmit={handleSubmit}>
              <label className="field-label">Full Name</label>
              <div className="input-group">
                <FaUser className="input-icon" />
                <input
                  type="text"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <label className="field-label">Email Address</label>
              <div className="input-group">
                <FaEnvelope className="input-icon" />
                <input
                  type="email"
                  placeholder="you@salon.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <label className="field-label">Password</label>
              <div className="input-group">
                <FaLock className="input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span
                  className="toggle-icon"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              {password && (
                <div className="password-strength">
                  <div className="strength-bar-track">
                    <div
                      className="strength-bar-fill"
                      style={{
                        width: `${(passwordStrength.passedCount / 4) * 100}%`,
                        background: passwordStrength.color,
                      }}
                    ></div>
                  </div>
                  <span
                    className="strength-label"
                    style={{ color: passwordStrength.color }}
                  >
                    {passwordStrength.label}
                  </span>

                  <ul className="password-rules">
                    <li className={passwordStrength.checks.length ? 'rule-pass' : 'rule-fail'}>
                      At least 8 characters
                    </li>
                    <li className={passwordStrength.checks.uppercase ? 'rule-pass' : 'rule-fail'}>
                      One uppercase letter
                    </li>
                    <li className={passwordStrength.checks.lowercase ? 'rule-pass' : 'rule-fail'}>
                      One lowercase letter
                    </li>
                    <li className={passwordStrength.checks.number ? 'rule-pass' : 'rule-fail'}>
                      One number
                    </li>
                  </ul>
                </div>
              )}

              <button type="submit" className="auth-btn" disabled={loading}>
  {loading ? (
    <>
      <span className="btn-spinner"></span> Creating Account...
    </>
  ) : (
    <>
      Create Account <FaArrowRight className="btn-icon" />
    </>
  )}
</button>
            </form>

            <p className="auth-switch">
              Already have an account? <Link to="/">Sign in</Link>
            </p>

            <p className="fine-print">
              By creating an account you agree to our{' '}
              <Link to="/terms">Terms of Service</Link> and{' '}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;